import React, { useCallback, useState } from 'react'
import { Text, View } from 'react-native'
import { CardEntryModel, routes } from '@linqpal/models'
import { useTranslation } from 'react-i18next'
import { flow } from '../../../Contractor/AddCreditDebitCard/flow'
import Wrapper from '../../../Contractor/Wrapper'
import { errorHandling } from '../../../Contractor/AddCreditDebitCard/errorHandling'
import { ErrorAlert } from '../../../Contractor/AddCreditDebitCard/ErrorAlert'
import BuilderBottomModal from '../../../../ui/molecules/BuilderBottomModal'
import Loading from '../../../Loading'
import { Spacer } from '@linqpal/components/src/ui'

export interface ErrorTypes {
  msg1: string
  msg2: string
  type: string
  action?: () => void
}

interface AddNewCreditCardFlow_IDLEProps {
  onSuccess: (data?: any) => void
  onClose: () => void
  customRoute?: (data: any) => Promise<any>
  setResponse: React.Dispatch<React.SetStateAction<any>>
  invoiceId: string
  phoneNumber?: string
}

export const AddNewCreditCardFlow_IDLE: React.FC<AddNewCreditCardFlow_IDLEProps> =
  ({
    onSuccess,
    onClose,
    customRoute,
    setResponse,
    invoiceId,
    phoneNumber,
  }) => {
    const { t } = useTranslation('global')

    const [loading, setLoading] = useState<boolean>(false)
    const [currentScreen, setCurrentScreen] = useState(flow.initial)
    const [creditCardModel, setCreditCardModel] = useState<any | null>(
      CardEntryModel.create({}),
    )
    const [error, setError] = useState<ErrorTypes>({
      msg1: '',
      msg2: '',
      type: '',
    })

    const addCard = useCallback(() => {
      setLoading(true)
      const route = customRoute || routes.company.addPaymentCard
      route({ ...creditCardModel.toJSON(), invoiceId, phoneNumber })
        .then(async (response) => {
          setResponse(response)
          onSuccess()
        })
        .catch((err) => {
          creditCardModel.stopSending()
          console.error(err)
          errorHandling(t, setError, err, setCurrentScreen)
        })
        .finally(() => {
          setLoading(false)
        })
    }, [
      customRoute,
      creditCardModel,
      onSuccess,
      setResponse,
      t,
      invoiceId,
      phoneNumber,
    ])

    const onAction = useCallback(() => {
      if (flow[currentScreen].next) {
        setCurrentScreen(flow[currentScreen].next)
      } else if (creditCardModel.canSave) {
        addCard()
      }
    }, [currentScreen, creditCardModel, addCard])

    const Component = flow[currentScreen].component

    return (
      <BuilderBottomModal
        onBack={
          flow[currentScreen].previous
            ? () => setCurrentScreen(flow[currentScreen].previous)
            : undefined
        }
        title={t('AddCard.link-a-card')}
        visible={true}
        onClose={onClose}
        testID={'add-new-credit-card-modal-idle'}
      >
        <Wrapper
          containerStyle={{
            width: '100%',
            alignItems: 'center',
            marginBottom: 30,
          }}
          // toolbar={toolbar}
        >
          <View testID={'add-new-credit-card-idle-content'}>
            {loading ? (
              <View
                style={{ height: 500 }}
                testID={'add-new-credit-card-loading'}
              >
                <Loading />
              </View>
            ) : (
              <>
                <Text
                  style={{
                    fontSize: 19,
                    fontWeight: '500',
                    textAlign: 'center',
                  }}
                  testID={`add-credit-card-title-${flow[currentScreen].title}`}
                >
                  {flow[currentScreen].title}
                </Text>
                <Component
                  testID={`add-credit-card-component-${flow[currentScreen].title}`}
                  onAction={onAction}
                  model={creditCardModel}
                />
              </>
            )}
            <Spacer height={16} />
            <ErrorAlert
              msg1={error.msg1}
              msg2={error.msg2}
              action={error.action}
              type={error.type}
              visible={!!error.type}
              onClose={() => {
                setCurrentScreen(flow.initial)
                setCreditCardModel(CardEntryModel.create({}))
                setError({
                  msg1: '',
                  msg2: '',
                  type: '',
                  action: undefined,
                })
              }}
            />
          </View>
        </Wrapper>
      </BuilderBottomModal>
    )
  }
